// Sample data + the key → (stage, day, chase shape) map the Message Templates
// screen previews with. Every email and SMS comes straight out of
// ./email-templates via getDailyTimeLine — the same call the daily job makes.

import { GROUPS } from "./email-templates";
import { BRANDS, type BrandKey } from "./email-templates/brands";
import getDailyTimeLine, {
  toEmailData,
  type Stage,
  type TimelineChase,
} from "./email-templates/timeline";

export { GROUPS };
export const BRAND_LIST = Object.values(BRANDS);
export type { BrandKey };

const RENEWAL_DATE = new Date("2026-07-15T00:00:00.000Z");
const COMPANY_NAME = "The Corner Cafe Ltd";
/** Sample policy reference — the "Pay online" CTA target on the priced emails. */
const POLICY_REF = "SME-000123";
/** Which chase on that reference — the pay link's `?version=`. */
const VERSION_REF = "V1";

/** Sample market comparison — Allianz recommended, £506.73/yr below the dearest. */
const QUOTES = [
  { insurerName: "Allianz", total: 410, selected: true },
  { insurerName: "AXA", total: 512.4, selected: false },
  { insurerName: "Aviva", total: 604.18, selected: false },
  { insurerName: "Ageas", total: 916.73, selected: false },
];

/** Template key prefix → which timeline table it reads and what chase it needs. */
const TRACKS: Record<string, { stage: Stage; auto?: boolean; monthly?: boolean }> = {
  A: { stage: "chase", auto: true },
  AD: { stage: "chase", auto: true, monthly: true },
  AC: { stage: "chase" },
  NA: { stage: "chase", monthly: true },
  WB: { stage: "win-back" },
  AN: { stage: "anniversary" },
  REN: { stage: "renewed" },
};

/** Logos are served from client/public/logos in the preview, not the send host. */
const localLogos = (html: string) => html.replace(/https?:\/\/[^"']*?\/logos\//g, "/logos/");

export type Preview = {
  key: string;
  badge: string;
  subject: string;
  html: string;
  sms: string | null;
};

const preview = (brand: BrandKey, key: string): Preview | null => {
  const [, prefix, days] = key.match(/^([A-Z]+)(\d+)$/) ?? [];
  const track = TRACKS[prefix];
  if (!track) return null;

  const day = Number(days);
  const chase: TimelineChase = {
    brand,
    autoRenew: !!track.auto,
    paymentType: track.monthly ? "INSTALMENTS" : "CASH",
    companyName: COMPANY_NAME,
    renewalDate: RENEWAL_DATE,
    instalments: track.monthly ? 10 : null,
    monthlyAmount: track.monthly ? 45.1 : null,
    totalPayable: track.monthly ? 451 : 410,
    quotes: QUOTES,
    unsubscribeUrl: "https://myportal.co.uk/unsubscribe/preview",
    policyRef: POLICY_REF,
    versionRef: VERSION_REF,
  };

  const data = getDailyTimeLine({ day, chase, stage: track.stage });
  if (!data?.email) return null;

  return {
    key,
    badge: track.stage === "renewed" ? "RENEWAL DAY" : `DAY ${day}`,
    subject: data.email.heading,
    html: localLogos(data.email.template(toEmailData(chase))),
    sms: data.sms?.message ?? null,
  };
};

export type PreviewGroup = (typeof GROUPS)[number] & { items: Preview[]; meta: string };

/** Every group, rendered for one brand — what the screen maps over. */
export const previewGroups = (brand: BrandKey): PreviewGroup[] =>
  GROUPS.map((group) => {
    const items = group.keys.map((key) => preview(brand, key)).filter(Boolean) as Preview[];
    const emails = `${items.length} email${items.length === 1 ? "" : "s"}`;
    return { ...group, items, meta: items.some((i) => i.sms) ? `${emails} + SMS` : emails };
  });
